import { useEffect, useState, lazy, Suspense } from 'react';
import { Home, Film, PlaySquare, Plus, User, Search, Bell, TrendingUp, Share2, LogIn, LogOut, FileText, Menu, X, ShieldCheck, BriefcaseBusiness, LifeBuoy, Trash2 } from 'lucide-react';
import { TabType, UserProfile, MediaHistoryItem, AppUser } from '../types';
import { BrandLogo } from '../features/shared/components/BrandLogo';
import { FollowButton } from '../features/shared/components/FollowButton';
import { FeedTab } from './FeedTab';
import { ShortsTab } from '../features/shorts/ShortsTab';
import { LongsTab } from '../features/longs/LongsTab';
import { ProfileTab } from '../features/profile/ProfileTab';
import { searchUsers, searchContent } from '../backend/searchService';

const CreateModal = lazy(() => import('../features/create/CreateModal').then(m => ({ default: m.CreateModal })));

interface WebViewProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  userProfile: UserProfile;
  onUpdateProfile: (updated: UserProfile) => void;
  mediaHistory: MediaHistoryItem[];
  onDeleteHistoryItem: (id: string) => void;
  onPublishMedia: (item: MediaHistoryItem) => void;
  currentUser?: AppUser | null;
  onOpenAuth?: () => void;
  onLogout?: () => void;
  onOpenAdmin?: () => void;
  onOpenPolicies?: () => void;
  onOpenFeedback: () => void;
  onOpenAppSupport: () => void;
}

export function WebView({
  activeTab,
  onTabChange,
  userProfile,
  onUpdateProfile,
  mediaHistory,
  onDeleteHistoryItem,
  onPublishMedia,
  currentUser,
  onOpenAuth,
  onLogout,
  onOpenAdmin,
  onOpenPolicies,
  onOpenFeedback,
  onOpenAppSupport
}: WebViewProps) {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [copiedLink, setCopiedLink] = useState(false);
  const [userSuggestions, setUserSuggestions] = useState<AppUser[]>([]);
  const [contentSuggestions, setContentSuggestions] = useState<any[]>([]);
  const [selectedProfileUid, setSelectedProfileUid] = useState<string | undefined>();

  useEffect(() => {
    if (searchQuery.trim().length >= 1) {
      const timer = setTimeout(() => {
        void searchUsers(searchQuery).then(setUserSuggestions);
        void searchContent(searchQuery).then(results => setContentSuggestions(results.filter(result => result.type !== 'channel')));
      }, 150);
      return () => clearTimeout(timer);
    }
    setUserSuggestions([]);
    setContentSuggestions([]);
  }, [searchQuery]);

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(`https://www.yetrykar.com/@${userProfile.username}`);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  return (
    <div className="flex h-screen w-full flex-col overflow-hidden bg-slate-50 text-slate-900">
      {/* Website Top Navigation Bar */}
      <header className="sticky top-0 z-30 flex shrink-0 items-center justify-between gap-4 border-b border-slate-200 bg-white px-4 py-2.5 sm:px-6">
        {/* Brand Logo */}
        <div
          onClick={() => onTabChange('home')}
          className="flex items-center gap-2.5 cursor-pointer select-none"
        >
          <button
            aria-label="Open navigation menu"
            onClick={() => setIsSidebarOpen(true)}
            className="md:hidden p-1.5 rounded-lg text-slate-600 hover:bg-slate-100"
          >
            <Menu className="w-5 h-5" />
          </button>
          <BrandLogo size="md" showDomain={true} />
        </div>

        {/* Global Search Bar */}
        <div className="flex-1 max-w-md hidden md:block">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search posts, creators, shorts or long videos..."
              className="w-full pl-9 pr-4 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:bg-white focus:border-slate-400 transition-all text-slate-900"
            />
            {searchQuery.trim() && (
              <div className="absolute left-0 right-0 top-full mt-2 z-40 rounded-xl border border-slate-200 bg-white shadow-xl overflow-hidden">
                {userSuggestions.length > 0 && (
                  <div className="border-b border-slate-100">
                    <p className="px-3 py-1.5 text-[9px] font-black uppercase tracking-[0.18em] text-slate-400 bg-slate-50">People</p>
                    {userSuggestions.slice(0, 5).map(user => (
                      <div
                        key={user.uid}
                        className="w-full px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-3"
                      >
                        <button type="button" className="flex items-center gap-2 min-w-0 text-left" onClick={() => {
                          setSearchQuery('');
                          setSelectedProfileUid(user.uid);
                          onTabChange('profile');
                        }}>
                          <img src={user.photoURL || userProfile.avatar} className="w-7 h-7 rounded-full object-cover" alt={user.displayName} />
                          <div className="min-w-0">
                            <div className="text-[11px] font-bold text-slate-900 truncate">{user.displayName}</div>
                            <div className="text-[9px] text-slate-500">@{user.username}</div>
                          </div>
                        </button>
                        <FollowButton
                          targetUid={user.uid}
                          targetName={user.displayName || user.username || 'creator'}
                          currentUser={currentUser}
                          onOpenAuth={onOpenAuth}
                          compact
                        />
                      </div>
                    ))}
                  </div>
                )}
                {contentSuggestions.length > 0 && (
                  <div>
                    <p className="px-3 py-1.5 text-[9px] font-black uppercase tracking-[0.18em] text-slate-400 bg-slate-50">Content</p>
                    {contentSuggestions.slice(0, 5).map(result => (
                      <button key={`${result.type}-${result.id}`} className="w-full px-3 py-2 text-left hover:bg-slate-50" onClick={() => setSearchQuery('')}>
                        <div className="text-[11px] font-bold text-slate-900 truncate">{result.title}</div>
                        <div className="text-[9px] text-slate-500 uppercase tracking-[0.12em]">{result.type}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Action icons & Create+ Button */}
        <div className="flex items-center gap-2.5">
          <button
            id="web-policies-btn"
            onClick={onOpenPolicies}
            className="hidden sm:flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50"
          >
            <FileText className="w-3.5 h-3.5" />
            <span>Policies</span>
          </button>
          <button
            id="web-create-btn"
            onClick={() => onTabChange('create')}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold cursor-pointer transition-colors ${
              activeTab === 'create'
                ? 'bg-blue-900 text-white'
                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
            }`}
          >
            <Plus className="w-3.5 h-3.5 stroke-[2.5px]" />
            <span>Create</span>
          </button>

          {/* User Auth Avatar / Login button */}
          {currentUser ? (
            <div className="flex items-center gap-2">
              <button
                id="web-profile-avatar-btn"
                onClick={() => onTabChange('profile')}
                className={`flex items-center gap-2 p-1 rounded-lg border transition-all cursor-pointer ${
                  activeTab === 'profile'
                    ? 'border-blue-900 ring-1 ring-blue-900/30'
                    : 'border-slate-200 hover:bg-slate-100'
                }`}
              >
                <img
                  src={currentUser.photoURL || userProfile.avatar}
                  alt={currentUser.displayName}
                  referrerPolicy="no-referrer"
                  className="w-6 h-6 rounded-full object-cover"
                />
                <span className="text-xs font-medium text-slate-700 pr-1 hidden lg:inline">
                  {currentUser.displayName.split(' ')[0]}
                </span>
              </button>

              <button
                onClick={onLogout}
                className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg cursor-pointer transition-colors"
                title="Log Out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              id="web-header-login-btn"
              onClick={onOpenAuth}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-900 hover:bg-blue-950 text-white rounded-lg text-xs font-semibold cursor-pointer shadow-2xs transition-all"
            >
              <LogIn className="w-3.5 h-3.5" />
              <span>Login / Sign Up</span>
            </button>
          )}
        </div>
      </header>

      {/* Main Grid: Left Navigation Sidebar + Content Viewport + Right Trending Sidebar */}
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Left Sidebar Navigation */}
        <aside className="hidden h-full w-52 shrink-0 select-none flex-col justify-between overflow-y-auto border-r border-slate-200 bg-white p-3 lg:flex">
          <div className="space-y-1">
            {[
              { id: 'home', label: 'Home Feed', icon: Home },
              { id: 'shorts', label: 'MiniS', icon: Film },
              { id: 'longs', label: 'Long Videos', icon: PlaySquare },
              { id: 'create', label: 'Create & Upload', icon: Plus },
              { id: 'profile', label: 'Profile & Settings', icon: User }
            ].map(item => {
              const Icon = item.icon;
              const isActive = activeTab === item.id;
              return (
                <button
                  key={item.id}
                  id={`web-nav-${item.id}`}
                  onClick={() => onTabChange(item.id as TabType)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs font-medium transition-all cursor-pointer ${
                    isActive
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-blue-400' : 'text-slate-400'}`} />
                  <span>{item.label}</span>
                </button>
              );
            })}

            <div className="pt-3 mt-3 border-t border-slate-100">
              <span className="text-[9px] uppercase font-semibold text-slate-400 tracking-wider px-3 block mb-1.5">
                Quick Shortcuts
              </span>
              <button
                onClick={() => onTabChange('profile')}
                className="w-full text-left px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg cursor-pointer"
              >
                Settings & Account
              </button>
              <button
                onClick={() => onTabChange('profile')}
                className="w-full text-left px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg cursor-pointer"
              >
                Media History ({mediaHistory.length})
              </button>
              <button
                onClick={onOpenPolicies}
                className="w-full text-left px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg cursor-pointer"
              >
                <ShieldCheck className="inline-block w-3.5 h-3.5 mr-2" /> Policies
              </button>
              <button
                type="button"
                onClick={onOpenFeedback}
                className="w-full text-left px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg cursor-pointer"
              >
                Feedback
              </button>
              <button
                type="button"
                onClick={onOpenAppSupport}
                className="w-full text-left px-3 py-1.5 text-xs text-slate-600 hover:text-slate-900 hover:bg-slate-50 rounded-lg cursor-pointer"
              >
                App Support
              </button>
            </div>
          </div>

          {/* User mini status */}
          <div className="p-2.5 bg-slate-50 rounded-xl border border-slate-200/80">
            <div className="flex items-center gap-2">
              <img
                src={userProfile.avatar}
                alt="Avatar"
                referrerPolicy="no-referrer"
                className="w-7 h-7 rounded-full object-cover"
              />
              <div className="min-w-0">
                <span className="text-xs font-medium text-slate-900 block truncate">
                  {userProfile.name}
                </span>
                <span className="text-[10px] text-slate-400 block truncate">
                  @{userProfile.username}
                </span>
              </div>
            </div>
          </div>
        </aside>

        {/* Center Main Stage */}
        <main data-screen-scroll-root className={`flex min-h-0 min-w-0 flex-1 justify-center bg-slate-50/50 ${activeTab === 'longs' ? 'p-0' : 'p-3 sm:p-5'} ${activeTab === 'home' || activeTab === 'profile' ? 'overflow-hidden' : 'overflow-y-auto'}`}>
          <div className={`w-full ${activeTab === 'longs' ? 'max-w-none overflow-visible bg-white dark:bg-slate-950' : 'max-w-2xl overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-2xs'} ${activeTab === 'home' || activeTab === 'profile' ? 'flex h-full min-h-0 flex-col' : ''}`}>
            {activeTab === 'home' && (
              <FeedTab
                mediaHistory={mediaHistory}
                onOpenAuth={onOpenAuth || (() => {})}
                currentUser={currentUser}
                onTabChange={onTabChange}
                onOpenProfile={uid => {
                  setSelectedProfileUid(uid);
                  onTabChange('profile');
                }}
              />
            )}
            {activeTab === 'shorts' && (
              <div className="max-w-xl mx-auto py-2">
                <ShortsTab mediaHistory={mediaHistory} currentUser={currentUser} onOpenAuth={onOpenAuth} />
              </div>
            )}
            {activeTab === 'longs' && (
              <LongsTab
                mediaHistory={mediaHistory}
                currentUser={currentUser}
                onOpenAuth={onOpenAuth}
                onToggleRightSidebar={() => setIsRightSidebarOpen(open => !open)}
              />
            )}
            {activeTab === 'create' && (
              <Suspense fallback={<div className="p-8 text-center text-xs font-semibold text-slate-500">Loading editor...</div>}>
                <CreateModal
                  currentUser={currentUser}
                  onGoLive={() => onTabChange('live')}
                  onPublish={item => {
                    onPublishMedia(item);
                    onTabChange('profile');
                  }}
                />
              </Suspense>
            )}
            {activeTab === 'profile' && (
              <ProfileTab
                profile={userProfile}
                onUpdateProfile={onUpdateProfile}
                mediaHistory={mediaHistory}
                onDeleteHistoryItem={onDeleteHistoryItem}
                currentUser={currentUser}
                onLogout={onLogout}
                onOpenAuth={onOpenAuth}
                viewingUserId={selectedProfileUid}
                onOpenProfile={uid => {
                  setSelectedProfileUid(uid);
                  onTabChange('profile');
                }}
                onOpenUpload={() => onTabChange('create')}
                onOpenFeedback={onOpenFeedback}
                onOpenAppSupport={onOpenAppSupport}
              />
            )}
          </div>
        </main>

        {/* Right Sidebar: Trending & Discover (Visible on XL screens) */}
        <aside className={`h-full w-64 shrink-0 overflow-y-auto border-l border-slate-200 bg-white p-4 ${
          activeTab === 'longs'
            ? (isRightSidebarOpen ? 'hidden min-[1280px]:block' : 'hidden')
            : 'hidden lg:block'
        }`}>
          {/* Trending Panel */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-slate-900 font-semibold text-xs">
              <TrendingUp className="w-3.5 h-3.5 text-blue-900" />
              <span>Trending Topics</span>
            </div>

            <div className="space-y-2">
              {[
                { tag: '#CleanMinimalism', count: '48.2k posts' },
                { tag: '#CinematicShorts', count: '124k videos' },
                { tag: '#CreativeStudio', count: '19.8k uploads' },
                { tag: '#yetrykar2026', count: '6.4k posts' }
              ].map(topic => (
                <div
                  key={topic.tag}
                  className="p-2 hover:bg-slate-50 rounded-lg cursor-pointer transition-colors"
                >
                  <span className="font-semibold text-xs text-slate-900 block">{topic.tag}</span>
                  <span className="text-[10px] text-slate-400">{topic.count}</span>
                </div>
              ))}
            </div>

            {/* Quick Share Profile Card with Yetrykar.com */}
            <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
              <div className="flex items-center gap-1.5">
                <Share2 className="w-3.5 h-3.5 text-blue-900" />
                <span className="text-xs font-semibold text-slate-900">Share Profile Link</span>
              </div>
              <p className="text-[10px] text-slate-500 leading-relaxed font-mono text-slate-600 truncate">
                https://www.yetrykar.com/@{userProfile.username}
              </p>
              <button
                onClick={handleCopyLink}
                className="w-full py-1.5 bg-blue-900 hover:bg-blue-950 text-white rounded-lg text-xs font-semibold cursor-pointer transition-colors shadow-2xs"
              >
                {copiedLink ? 'Link Copied!' : 'Copy Profile Link'}
              </button>
            </div>

            <div className="border-t border-slate-100 pt-4 space-y-2 text-[10px] text-slate-500">
              <a href="mailto:support@yetrykar.com" className="flex items-center gap-2 hover:text-slate-900">
                <LifeBuoy className="w-3.5 h-3.5" /> support@yetrykar.com
              </a>
              <button className="flex items-center gap-2 hover:text-slate-900">
                <BriefcaseBusiness className="w-3.5 h-3.5" /> Careers
              </button>
              <button className="flex items-center gap-2 hover:text-slate-900">
                <ShieldCheck className="w-3.5 h-3.5" /> Compliance &amp; Safety
              </button>
              <span className="block pt-1 text-[9px] text-slate-400">Community Guidelines · Help Center · Privacy</span>
            </div>
          </div>
        </aside>
      </div>
      {isSidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <button aria-label="Close navigation menu" className="absolute inset-0 bg-slate-950/40" onClick={() => setIsSidebarOpen(false)} />
          <aside className="relative h-full w-72 bg-white p-5 shadow-2xl">
            <div className="flex items-center justify-between">
              <BrandLogo size="md" showDomain={true} />
              <button aria-label="Close navigation menu" onClick={() => setIsSidebarOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <nav className="mt-8 space-y-2">
              {[
                { id: 'home', label: 'Home Feed', icon: Home },
                { id: 'shorts', label: 'Shorty', icon: Film },
                { id: 'longs', label: 'Long Videos', icon: PlaySquare },
                { id: 'profile', label: 'Profile & Settings', icon: User }
              ].map(item => {
                const Icon = item.icon;
                return (
                  <button key={item.id} onClick={() => { onTabChange(item.id as TabType); setIsSidebarOpen(false); }} className="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100">
                    <Icon className="w-4 h-4" />{item.label}
                  </button>
                );
              })}
              <button onClick={() => { onOpenPolicies?.(); setIsSidebarOpen(false); }} className="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100 cursor-pointer">
                <ShieldCheck className="w-4 h-4" />Policies
              </button>
              <a href="/delete-account" className="w-full flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-semibold text-rose-600 hover:bg-rose-50 cursor-pointer">
                <Trash2 className="w-4 h-4" />Delete Account
              </a>
            </nav>
            <div className="mt-8 border-t border-slate-100 pt-5 space-y-2 text-xs text-slate-500">
              <a href="mailto:support@yetrykar.com" className="flex items-center gap-2 hover:text-slate-900"><LifeBuoy className="w-4 h-4" /> support@yetrykar.com</a>
              <span className="flex items-center gap-2"><BriefcaseBusiness className="w-4 h-4" /> Careers</span>
              <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Compliance & Safety</span>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
